import { Draft, produce } from 'immer';
import { useCallback } from 'react';
import useStitchedResult from 'hooks/useStitchedResult';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import {
  Price,
  WeekInfo,
  MarketSellerSection,
  ProductTypeSection,
  ProductWeightSection,
  ProductData,
  Supply,
  SupplyDate,
} from 'generated/graphql';

const stitchSeasonPlan = (weekInfo: WeekInfo[], weekInfoNew: WeekInfo[]) => {
  const indexesToSplice: Number[] = [];

  const stitchedDates = weekInfo.map((info) => {
    const index = weekInfoNew.findIndex((curr) => curr.start === info.start);
    if (index === -1) {
      return { ...info };
    }

    indexesToSplice.push(index);
    return {
      ...weekInfoNew[index],
    };
  });

  const dataToAdd = weekInfoNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedDates || []), ...(dataToAdd || [])];
};

const stitchDeliveryDates = (dates: SupplyDate[], datesNew: SupplyDate[]) => {
  const indexesToSplice: Number[] = [];

  const stitchedDates = dates.map((date) => {
    const index = datesNew.findIndex((curr) => curr.csvDate === date.csvDate);
    if (index === -1) {
      return { ...date };
    }

    indexesToSplice.push(index);
    return {
      ...datesNew[index],
    };
  });

  const dataToAdd = datesNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedDates || []), ...(dataToAdd || [])];
};

const stitchSupply = (supply: Supply, supplyNew: Supply): Supply => {
  if (!supply || !supplyNew) {
    return supplyNew;
  }

  return {
    ...supplyNew,
    dates: stitchDeliveryDates(supply.dates, supplyNew.dates),
    marketDates: stitchDeliveryDates(supply.marketDates, supplyNew.marketDates),
    seasonPlan: stitchSeasonPlan(supply.seasonPlan, supplyNew.seasonPlan),
  };
};

const stitchPrices = (prices: Price[], pricesNew: Price[]): Price[] => {
  const indexesToSplice: Number[] = [];

  const stitchedPrices = prices.map((price) => {
    const index = pricesNew.findIndex((curr) => curr.code === price.code);
    if (index === -1) {
      return { ...price };
    }

    indexesToSplice.push(index);
    return {
      ...price,
    };
  });

  const dataToAdd = pricesNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedPrices || []), ...(dataToAdd || [])];
};

const stitchProductData = (products: ProductData[], productsNew: ProductData[]): ProductData[] => {
  const indexesToSplice: Number[] = [];
  const stitchedProducts = products.map((product) => {
    const index = productsNew.findIndex((curr) => curr.sku === product.sku);
    if (index === -1) {
      return { ...product };
    }

    const productNew = productsNew[index];
    indexesToSplice.push(index);
    return {
      ...productNew,
      prices: stitchPrices(product.prices, productNew.prices),
      supply: stitchSupply(product.supply, productNew.supply),
    };
  });

  const dataToAdd = productsNew.filter((_, index) => !indexesToSplice.includes(index));
  return [...(stitchedProducts || []), ...(dataToAdd || [])];
};

const stitchProductWeights = (
  productWeights: ProductWeightSection[],
  productWeightsNew: ProductWeightSection[],
): ProductWeightSection[] => {
  const indexesToSplice: Number[] = [];
  const stitchedProductWeights = productWeights.map((productWeight) => {
    const index = productWeightsNew.findIndex((curr) => curr.name === productWeight.name);
    if (index === -1) {
      return { ...productWeight };
    }

    indexesToSplice.push(index);

    const stitchedProducts = stitchProductData(
      (productWeight?.data?.products as ProductData[]) ?? [],
      (productWeightsNew?.[index]?.data?.products as ProductData[]) ?? [],
    );

    return {
      ...productWeightsNew?.[index],
      data: {
        ...productWeightsNew?.[index].data,
        products: stitchedProducts,
      },
    };
  }) as ProductWeightSection[];

  const dataToAdd = productWeightsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedProductWeights || []), ...(dataToAdd || [])];
};

// Stiching product types
const stitchProductTypes = (
  sections: ProductTypeSection[],
  sectionsNew: ProductTypeSection[],
): ProductTypeSection[] => {
  const indexesToSplice: Number[] = [];
  const stitchedSections = sections.map((section) => {
    const index = sectionsNew.findIndex((curr) => curr.productTypeID === section.productTypeID);
    if (index === -1) {
      return { ...section };
    }

    const sectionNew = sectionsNew[index];
    indexesToSplice.push(index);

    return {
      ...section,
      buttons: sectionNew.buttons || section.buttons,
      productTypeEnabled: sectionNew.productTypeEnabled || section.productTypeEnabled,
      productWeights: stitchProductWeights(section.productWeights, sectionNew.productWeights),
    };
  });

  const dataToAdd = sectionsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(stitchedSections || []), ...(dataToAdd || [])];
};
const stitchProducts = (
  productSections: MarketSellerSection | undefined,
  productSectionsNew: MarketSellerSection,
): MarketSellerSection => {
  if (!productSections || !productSectionsNew) {
    return productSectionsNew;
  }

  const sections = stitchProductTypes(productSections.sections, productSectionsNew.sections);
  const suggestions = new Set([...(productSections.suggestions ?? []), ...productSectionsNew.suggestions]) ?? new Set();

  return {
    ...productSections,
    suggestions: [...suggestions],
    sections: sections,
    pageInfo: productSectionsNew.pageInfo,
  };
};

export const useStitchProductsNew = (data: MarketSellerSection | undefined, allowReset: boolean) => {
  const [productSections, setProductSections] = useStitchedResult(data, (previous, next) =>
    next.pageInfo.page === DEFAULT_PAGE_NUMBER && allowReset ? next : stitchProducts(previous, next),
  );

  const set = useCallback(
    (updater: (draft: Draft<MarketSellerSection | undefined>) => void) => {
      setProductSections(produce(updater));
    },
    [setProductSections],
  );

  return {
    products: productSections,
    set,
    setProductSections,
  };
};
